import React, { useEffect, useState, useRef } from "react";
import { Box, Paper, Typography, Stack, alpha } from "@mui/material";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import PdfGenerator from "./Pdf/CaSummaryPdf";
import ScheduleHeader from "../../App/ScheduleHeader";
import NoDataAvailableUI from "../../Common/NoDataAvailable";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../utils/export/componentExporter";

const isNetBalancingAllowanceRow = (section = {}) =>
  String(section.caSummary || "").toLowerCase() === "net balancing allowance";

const getAmount = (value) => Number(value) || 0;

const SummaryList = ({ summary, netTotal }) => {
  if (!summary || summary.length === 0) {
    return (
      <NoDataAvailableUI
        title='No CA Summary Available'
        content='Your Capital Allowances summary will be generated once you add asset data or import existing records in relevant schedule.'
        subContent='This summary will show your available allowances, written down values, and calculations after data is added.'
      />
    );
  }

  const breakdownSummary = summary;

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.divider, 0.12),
          bgcolor: "background.paper",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent='space-between'
          spacing={3}
        >
          <Box>
            <Typography
              variant='h6'
              sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
            >
              Capital Allowance Summary
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Total deductions for tax computation
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ minWidth: { md: 260 } }}
          >
            <Box
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ fontWeight: 700, textTransform: "uppercase" }}
              >
                Total Deductible Amount
              </Typography>
              <Typography
                variant='h4'
                sx={{
                  mt: 0.75,
                  fontWeight: 800,
                  color: "text.primary",
                }}
              >
                {formatAmount(netTotal)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {breakdownSummary.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {breakdownSummary.map((section, index) => {
            const isBalancingAllowance = isNetBalancingAllowanceRow(section);
            const initialAllowance = getAmount(section.initialAllowance);
            const annualAllowance = getAmount(section.annualAllowance);
            const balancingAllowance = getAmount(section.bcBa);
            const total = getAmount(section.total);
            const details = isBalancingAllowance
              ? [
                  {
                    label: "BA / (BC)",
                    amount: balancingAllowance,
                    visible: true,
                  },
                ]
              : [
                  {
                    label: "Initial Allowance",
                    amount: initialAllowance,
                  },
                  {
                    label: "Annual Allowance",
                    amount: annualAllowance,
                  },
                ].map((item) => ({
                  ...item,
                  visible: item.amount !== 0,
                }));

            return (
              <Paper
                key={`${section.caSummary}-${index}`}
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.12),
                  bgcolor: "background.paper",
                  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Stack spacing={2} sx={{ height: "100%" }}>
                  <Stack spacing={0.5}>
                    <Typography
                      variant='subtitle1'
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {section.caSummary}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Allowance breakdown
                    </Typography>
                  </Stack>

                  <Stack spacing={1.25} sx={{ minHeight: 92 }}>
                    {details.map((item) => (
                      <Stack
                        key={item.label}
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        spacing={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: (theme) =>
                            alpha(theme.palette.action.hover, 0.12),
                          visibility: item.visible ? "visible" : "hidden",
                        }}
                      >
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontWeight: 600 }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          {formatAmount(item.amount)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: (theme) =>
                        alpha(theme.palette.divider, 0.12),
                    }}
                  >
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      spacing={2}
                    >
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        Sub Total
                      </Typography>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 800, color: "primary.main" }}
                      >
                        {formatAmount(total)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}
    </Stack>
  );
};

const CaSummary = ({
  companyName,
  uen,
  yearOfAssessment,
  financialYearStart,
  financialYearEnd,
  schedule,
  scheduleDetails,
  deleteSchedule,
  canEdit = true,
}) => {
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "CA Summary");
    }
  };

  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [summary, setSummary] = useState([]);

  const [netTotal, setNetTotal] = useState(0);

  useEffect(() => {
    //init
    if (schedule) {
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      if (scheduleDetails) {
        setSummary(scheduleDetails.summary);

        let netSum = 0;

        if (scheduleDetails.summary && scheduleDetails.summary.length > 0)
          netSum = scheduleDetails.summary.reduce(
            (sum, row) => sum + row.total,
            0
          );

        setNetTotal(netSum);
      }
    }
  }, []);

  return (
    <Box sx={{ padding: 4, minHeight: "100vh" }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName,
          uen,
          yearOfAssessment,
          financialYearStart,
          financialYearEnd,
          deleteSchedule,
          canEditSchedule: canEdit,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={{
            name: companyName,
            uen: uen,
            yearOfAssessment: yearOfAssessment,
            financialYearStart: financialYearStart,
            financialYearEnd: financialYearEnd,
          }}
          summary={summary}
          netTotal={netTotal}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the CA Summary'
          />
        </Box>

        <Box ref={exportRef}>
          <SummaryList summary={summary} netTotal={netTotal} />
        </Box>
      </Box>
    </Box>
  );
};

export default CaSummary;
